import { 
  List,
  ListItem,
  CircularProgress,
  Typography,
  Box,
  Button,
} from '@material-ui/core';
import { Refresh as RefreshIcon } from '@material-ui/icons';
import { dateFormat } from 'utils/formater';
import { Divider } from 'components';
import { 
  useCallback, 
  useEffect, 
  useRef, 
  useState,
} from 'react';
import PropTypes from 'prop-types';
import useStyles from './style';
import MessageItem from './MessageItem';

const MessageList = (props) => {
  const {
    messages,
    setMessages,
    totalMessages,
    loadMore,
    onEdit,
    events,
    onDelete,
    isLoading: initialIsLoading = false,
    loadError: initialLoadError = false,
    permissions,
    shouldScrollToBottom,
    setShouldScrollToBottom,
  } = props;

  const classes = useStyles();
  const listRef = useRef(null);
  const [isLoading, setIsLoading] = useState(initialIsLoading);
  const [loadError, setLoadError] = useState(initialLoadError);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 15,
    hasMore: messages.length < totalMessages,
  });
  const messagesEndRef = useRef(null);

  const groupMessagesByDate = (messages) => {
    const groups = {};
    
    messages.forEach((message) => {
      const messageDate = new Date(message.date);
      const dateKey = messageDate.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      });
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      
      groups[dateKey].push(message);
    });
    
    return groups;
  };
  
  const groupedMessages = groupMessagesByDate(messages);

  const scrollToBottom = (smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ 
      ...(smooth && {
        behavior: 'smooth',
      }),
    });
  };

  const loadData = () => {
    const nextPage = pagination.page + 1;
    setIsLoading(true);
    setLoadError(false);
    const prevScrollHeight = listRef.current.scrollHeight;

    loadMore({
      page: messages.length === 0 ? 1 : nextPage,
      limit: pagination.limit,
    }).then((newMessages) => {
      setPagination((prev) => ({
        ...prev,
        page: messages.length === 0 ? 1 : nextPage,
        hasMore: (newMessages?.length || 0) + messages.length < totalMessages,
      }));
      setMessages((prev) => {
        const updatedMessages = [...newMessages || [], ...prev];
        
        requestAnimationFrame(() => {
          const newScrollHeight = listRef.current.scrollHeight;
          listRef.current.scrollTop = newScrollHeight - prevScrollHeight - 40;
        });
        
        return updatedMessages;
      });
      setLoadError(false);
    }).catch(() => {
      setLoadError(true);
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const handleScroll = useCallback(() => {
    if (listRef.current && !isLoading && pagination.hasMore && !loadError) {
      const { scrollTop } = listRef.current;
      const isNearTop = scrollTop < 100;
      
      if (isNearTop && pagination?.hasMore) {
        setShouldScrollToBottom(false);
        setIsLoading(true);
        loadData();
      }
    }
  }, [isLoading, pagination, loadMore, loadError, messages.length, totalMessages]);

  useEffect(() => {
    setLoadError(false);

    setPagination((prev) => ({
      ...prev,
      ...(messages.length <= pagination.limit && {
        page: 1,
      }),
      hasMore: messages.length < totalMessages,
    }));

    if (messages.length > 0 && pagination.page === 1) {
      scrollToBottom(false);
    } else if (shouldScrollToBottom) {
      scrollToBottom();
    } else if (messages.length === 0) {
      setPagination((prev) => ({
        ...prev,
        page: 1,
        hasMore: false,
      }));
    }
  }, [messages]);

  useEffect(() => {
    setIsLoading(initialIsLoading);
  }, [initialIsLoading]);

  useEffect(() => {
    setLoadError(initialLoadError);
  }, [initialLoadError]);

  return (
    <Box 
      className={classes.messagesContainer}
      ref={listRef}
      onScroll={handleScroll}
      style={{
        ...(!permissions.add && {
          paddingBottom: '0px',
        }),
      }}
    >
      <List style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: messages.length === 0 && (isLoading || loadError) ? 'center' : 'flex-end',
        minHeight: '100%',
      }}>
        {isLoading && !loadError && (
          <ListItem style={{ 
            justifyContent: 'center',
          }}>
            <CircularProgress size={24} />
          </ListItem>
        )}
        {loadError && (
          <ListItem style={{ 
            justifyContent: 'center',
            flexDirection: 'column',
            gap: '8px',
          }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              size="small"
            >
              Reintentar
            </Button>
          </ListItem>
        )}

        {messages.length === 0 && !isLoading && !loadError ? (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            textAlign="center"
            style={{ 
              height: '80dvh',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              ¡Sé el primero en dejar un mensaje!
            </Typography>
          </Box>
        ) : (
          Object.entries(groupedMessages).map(([date, messages], index) => (
            <Box key={index}>
              <Box style={{ 
                marginBlock: '10px',
              }}>
                <Divider 
                  children={dateFormat(messages[0].date, 'DD/MM/YYYY', true)} 
                  align='center' 
                />
              </Box>
              
              {messages.map((message, index) => (
                <MessageItem
                  key={index}
                  message={message}
                  permissions={permissions}
                  onEdit={onEdit}
                  events={events}
                  onDelete={onDelete}
                  setMessages={setMessages}
                />
              ))}
            </Box>
          ))
        )}
        <Box ref={messagesEndRef} />
      </List>
    </Box>
  );
};

MessageList.propTypes = {
  messages: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      text: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      user: PropTypes.string.isRequired,
    }),
  ),
  setMessages: PropTypes.func.isRequired,
  totalMessages: PropTypes.number,
  loadMore: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
  events: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
      icon: PropTypes.element,
      disabled: PropTypes.bool,
    }),
  ),
  onDelete: PropTypes.func,
  isLoading: PropTypes.bool,
  loadError: PropTypes.bool,
  permissions: PropTypes.shape({
    add: PropTypes.bool,
    edit: PropTypes.bool,
    delete: PropTypes.bool,
  }),
  shouldScrollToBottom: PropTypes.bool,
  setShouldScrollToBottom: PropTypes.func,
};

MessageList.defaultProps = {
  messages: [],
  totalMessages: 0,
  onEdit: null,
  events: [],
  onDelete: null,
  isLoading: false,
  loadError: false,
  permissions: {
    add: false,
    edit: false,
    delete: false,
  },
  shouldScrollToBottom: false,
  setShouldScrollToBottom: () => {},
};

export default MessageList;
